import type { CheckoutPerBookingService, ServiceMapper, ServiceType } from '../../../domain/types'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type { APIBookingDraft, APIBookingDraftService } from '@/scopes/checkout/infrastructure/api/types'
import { isNil, values } from 'ramda'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceBannersMapper, type ServiceBannersMapper } from '../model-mappers/serviceBannersMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '../model-mappers/serviceParticipantsMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceOptionMapper?: ServiceOptionMapper
  serviceBannersMapper?: ServiceBannersMapper
  serviceType: ServiceType
}

export const createPerBookingServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutPerBookingService> = (
  dependencies,
) => {
  const {
    serviceName,
    serviceType,
    serviceTypeMapper,
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceOptionMapper = createServiceOptionMapper(),
    serviceBannersMapper = createServiceBannersMapper(),
  } = dependencies

  function map(bookingDraft: APIBookingDraft, genericServiceAName?: string) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName || aName === genericServiceAName)
    if (!service)
      return null

    const { aName, options } = service
    const selectedValue = mapSelectedValue(service)

    return {
      id: aName,
      isSelected: selectedValue ? Boolean(selectedValue) : !isNil(selectedValue),
      type: serviceType,
      selectedValue,
      participants: serviceParticipantsMapper.map(service, bookingDraft),
      options: options.map(option => serviceOptionMapper.map(option, service, bookingDraft)),
      banners: serviceBannersMapper.map(service),
      isPricePerPerson: service.isPricePerPerson,
    }
  }

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function mapSelectedValue({ options }: APIBookingDraftService) {
    return options.find(({ isSelected }) => isSelected)?.code ?? null
  }

  function mapSelection(service: CheckoutPerBookingService) {
    const { participants } = service

    return Object.values(participants ?? {}).map(({ id, selectedValue }) => ({
      participantId: id,
      value: selectedValue,
    }))
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutPerBookingService, step: number): ImpressionViewAncillariesItem[] {
    if (!service.selectedValue)
      return []

    const option = service.options.find(({ value }) => value === service.selectedValue)

    if (!option)
      return []

    const input: SelectedItemInput = {
      label: option.label,
      value: option.value,
      price: option.price,
      rawPrice: option.rawPrice,
      badge: option.badge,
      ...(service.isPricePerPerson ? { quantity: String(values(service.participants ?? {}).length) } : {}),
    }

    return mapDefaultSelectedItemToGA4([input], {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
